<template>
    <div class="transcription-container p-6 md:p-8 relative">
        <div class="flex-col items-center space-y-5">
            <div>
                <div class="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
                    <p class="text-base md:text-lg font-bold flex items-center gap-5 mb-5 md:mb-0">
                        {{ trans('global.system_name') }} {{ trans('modules.Ai.name') }}
                        <i class="fad fa-sparkles" style="--fa-primary-color: #800b72; --fa-secondary-color: var(--primary-color); --fa-secondary-opacity: 1;"></i>
                    </p>
                    <!-- buttons -->
                    <div class="flex flex-row items-center space-x-3 w-full md:w-auto">
                        <ep-button
                            v-if="!transcriptionIsRecording"
                            @click="startRecording"
                            :start-icon="isMobileView ? null : 'play'"
                            :disabled="!selectedLanguage || missingAudioPermission"
                            class="flex-grow"
                        >
                            {{ selectedLanguage ? (hasTranscribedContent ? trans('modules.Ai.resume_recording') : trans('modules.Ai.start_recording')) : trans('modules.Ai.select_language_first') }}
                        </ep-button>

                        <button
                            v-if="transcriptionIsRecording"
                            @click="stopRecording"
                            class="btn btn-danger flex-grow"
                        >
                            <i class="fa fa-circle m-r-5 rounded-full animate-pulse hidden md:inline-block"></i>
                            {{ trans('modules.Ai.stop_recording') }}
                        </button>

                        <div class="transcription-settings-menu-container relative">
                            <button class="dropdown-toggle btn btn-trans whitespace-nowrap" @click.prevent="openSettings">
                                <i class="fas fa-exclamation-triangle mr-3 text-danger" v-if="missingAudioPermission"></i>
                                {{ trans('global.settings') }}
                                <i class="ml-3 fa fa-cogs hidden md:inline-block"></i>
                            </button>
                            <div v-if="settingsOpened" class="transcription-settings-menu flex flex-col gap-6 rounded py-6 shadow-md">
                                <div class="px-6">
                                    <label for="spoken_language">{{ trans('modules.Ai.spoken_language') }}</label>
                                    <select
                                        id="spoken_language"
                                        name="spoken_language"
                                        v-model="selectedLanguage"
                                        class="w-full form-control"
                                        :disabled="transcriptionIsRecording"
                                    >
                                        <option v-for="language in supportedLanguages" :key="language.id" :value="language">{{ language.text }}</option>
                                    </select>
                                </div>
                                <audio-device-select
                                    :is-recording="transcriptionIsRecording"
                                    :show-audio-meter="settingsOpened"
                                />
                                <div class="px-6">
                                    <switchery name="show_timestamps" v-model="showTimestamps" :text="trans('modules.Ai.show_timestamps')" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="! hasTranscribedContent && ! editingTranscription" class="n-color-text-weak text-sm md:text-base">
                    <p>{{ trans('modules.Ai.journal_transcription_desc') }}</p>
                    <p class="mb-0">{{ trans('modules.Ai.journal_transcription_cta') }}</p>
                    <p v-if="canEditTranscription" class="mt-5 mb-0">
                        {{ trans('modules.Ai.transcript_manual_add_before') }}<a
                            href="#"
                            @click.prevent="editTranscription"
                            class="underline"
                        >{{ trans('modules.Ai.transcript_manual_add') }}
                        </a>{{ trans('modules.Ai.transcript_manual_add_after') }}
                    </p>
                </div>

                <div
                    v-if="hasTranscribedContent || editingTranscription"
                    class="text-sm bg-white bg-opacity-50 n-color-text-weak mt-5 border border-solid border-grey-200 rounded overflow-hidden py-5 px-6"
                    :class="[collapseTranscription ? 'truncate-content' : '']"
                >
                    <div class="flex items-center justify-between">
                        <div>
                            <strong>{{ trans('modules.Ai.transcription_name') }}: </strong>
                            <span v-if="collapseTranscription">... {{ lastTranscriptionWords }}</span>
                            <span v-if="collapseTranscription" class="n-color-text-weaker italic">{{ transcriptionUnstableChunk }}</span>
                        </div>
                        <div class="flex gap-7">
                            <a href="#" @click.prevent="collapseTranscription = !collapseTranscription" class="flex items-center whitespace-nowrap">
                                {{ collapseTranscription ? `+ ${trans('modules.Ai.transcription_show')}` : `- ${trans('modules.Ai.transcription_hide')}` }}
                            </a>
                        </div>
                    </div>
                    <div v-if="editingTranscription" class="mt-3">
                        <textarea
                            id="transcription-editor"
                            :value="fullTranscriptionForEditing"
                            @input="onUpdatedFullTranscription($event.target.value)"
                            @blur="editingTranscription = false"
                            class="form-control w-full"
                            rows="8"
                            placeholder="Edit your transcription here"
                        />
                    </div>
                    <div v-if="!collapseTranscription && !editingTranscription" ref="transcriptionContainer" class="mt-3" style="max-height: 300px; overflow-y: auto;">
                        <template v-for="(chunk, index) in transcriptionChunks" :key="index">
                            <div
                                v-if="chunk.text && chunkIndexBeingEdited !== index"
                                class="n-color-text-weak py-1 px-2 hover:bg-grey-100 rounded cursor-pointer"
                                @click.prevent="editChunkAtIndex(index)"
                            ><span v-if="showTimestamps" class="n-color-text-weaker mr-5 select-none">{{ formatTime(chunk.timestamp) }}</span><span>{{ chunk.text }}</span></div>
                            <div v-if="chunkIndexBeingEdited === index" class="flex items-center px-2">
                                <span v-if="showTimestamps" class="n-color-text-weaker mr-5 select-none">{{ formatTime(chunk.timestamp) }}</span>
                                <input
                                    id="chunkEditingInput"
                                    type="text"
                                    class="form-control"
                                    v-model="chunk.text"
                                    @blur="finishEditingChunkAtIndex(index)"
                                    @keydown.enter="finishEditingChunkAtIndex(index)"
                                />
                            </div>
                        </template>
                        <div
                            v-if="transcriptionUnstableChunk"
                            class="n-color-text-weaker italic py-1 px-2 hover:bg-grey-100 rounded"
                        ><span v-if="showTimestamps" class="mr-5 select-none">{{ formatTime(new Date()) }}</span><span>{{ transcriptionUnstableChunk }}</span></div>
                    </div>
                </div>

                <div v-if="hasTranscribedContent" class="flex items-center justify-end mt-8">
                    <div class="flex items-center space-x-5">
                        <span v-if="summaryTemplates.length === 0" class="w-full text-gray-600 text-sm">
                            No summary templates available for your profession. Please update your profile.
                        </span>
                        <select
                            v-else
                            id="summary_template"
                            name="summary_template"
                            v-model="summaryTemplate"
                            class="w-full form-control m-b-0"
                            :disabled="transcriptionIsRecording"
                        >
                            <option v-for="template in summaryTemplates" :key="template.key" :value="template">{{ template.label }}</option>
                        </select>
                        <div>
                            <nord-button
                                @click.prevent="generateSummary"
                                :loading="loadingSummary"
                                :disabled="transcriptionIsRecording || summaryTemplates.length === 0"
                            >
                                {{ trans('modules.Ai.generate_summary') }}
                            </nord-button>
                        </div>
                    </div>
                </div>
                <p class="text-right text-sm text-grey-600 m-b-0 mt-5" v-if="showAiSummaryDisclaimer">
                    <i class="far fa-exclamation-circle mr-2"></i>
                    {{ trans('modules.Ai.generated_summary_disclaimer') }}
                </p>
            </div>

        </div>

        <elevator-loading-state
            v-if="loadingSummary"
            :options="elevatorNoteOptions"
            :speed-in-ms="elevatorNoteSpeed"
            class="absolute inset-0"
        />
    </div>
</template>

<script>
import Switchery from 'Components/global/form/Switchery.vue';
import AudioDeviceSelect from 'Components/global/ai/AudioDeviceSelect.vue';
import canEditTranscription from 'Components/global/ai/mixins/canEditTranscription';
import { http } from '@/system/http';
import EpButton from 'Components/global/button/index.vue';
import ElevatorLoadingState from 'Components/global/ai/ElevatorLoadingState.vue';
import { SummaryPromptType } from './constants';
import useAudioDevices from './composables/useAudioDevices';
import useTranscriptionSettings from './composables/useTranscriptionSettings';
import useTranscriptionService from './composables/useTranscriptionService';
import Brands from '@/core/Brands';

export default {
    name: 'JournalTranscriptionForm',

    components: {
        ElevatorLoadingState,
        EpButton,
        Switchery,
        AudioDeviceSelect,
    },

    mixins: [
        canEditTranscription,
    ],

    props: {
        journalId: {
            required: false,
        },
        hasPreviousJournalEntries: {
            required: false,
            default: false,
        },
    },

    setup() {
        // Audio devices composable
        const {
            missingAudioPermission,
            fetchAudioDevices,
        } = useAudioDevices();

        // Transcription settings composable
        const {
            language: selectedLanguage,
            showTimestamps,
            supportedLanguages,
            defaultLanguage,
        } = useTranscriptionSettings();

        // Transcription service composable
        const {
            isRecording: transcriptionIsRecording,
            unstableChunk: transcriptionUnstableChunk,
            startRecording: startTranscriptionRecording,
            stopRecording: stopTranscriptionRecording,
            cleanup: cleanupTranscription,
            setCallbacks,
        } = useTranscriptionService();

        return {
            // Audio device for recording
            missingAudioPermission,
            fetchAudioDevices,

            // Transcription settings
            selectedLanguage,
            showTimestamps,
            supportedLanguages,
            defaultLanguage,

            // Transcription service
            transcriptionIsRecording,
            transcriptionUnstableChunk,
            startTranscriptionRecording,
            stopTranscriptionRecording,
            cleanupTranscription,
            setTranscriptionCallbacks: setCallbacks,
        };
    },

    data() {
        return {
            transcript: null,
            transcriptionChunks: [],
            summaryTemplates: [],
            summaryTemplate: null,
            summary: null,
            loadingSummary: false,
            collapseTranscription: false,
            settingsOpened: false,
            transcriptionSyncInterval: null,
            persistingChunks: false,
            chunkIndexBeingEdited: null,
            showAiSummaryDisclaimer: false,
            editingTranscription: false,
            isMobileView: false,
        };
    },

    mounted() {
        // Set initial mobile view state
        this.checkMobileView();

        // Listen for window resize to update mobile state
        window.addEventListener('resize', this.checkMobileView);

        this.$event_bus.$on('closeCreateNewJournal', async () => {
            if (this.transcriptionIsRecording) {
                await this.stopRecording();
            }
            this.cleanupTranscription();
            this.transcript = null;
            this.transcriptionChunks = [];
            this.summary = null;
            this.collapseTranscription = this.isMobileView;
            this.showAiSummaryDisclaimer = false;
        });

        // Close the dropdown
        $(document).mousedown((e) => {
            const container = $(".transcription-settings-menu-container .transcription-settings-menu, .transcription-settings-menu-container .dropdown-toggle, .sweet-alert.showSweetAlert.visible");

            // if the target of the click isn't the container nor a descendant of the container or a swal
            if (!container.is(e.target) && container.has(e.target).length === 0) {
                this.settingsOpened = false;
            }
        });

        this.transcriptionSyncInterval = setInterval(() => {
            this.persistTranscriptionChunks();
        }, 5000);

        // Set up transcription service callbacks
        this.setTranscriptionCallbacks({
            onChunkReady: this.handleChunkReady,
            onUnstableChunk: this.handleUnstableChunk
        });

        // Fetch summary templates from the backend
        this.fetchSummaryTemplates();
    },

    beforeUnmount() {
        window.removeEventListener('resize', this.checkMobileView);

        if (this.transcriptionSyncInterval) {
            clearInterval(this.transcriptionSyncInterval);
            this.transcriptionSyncInterval = null;
        }
    },

    methods: {
        checkMobileView() {
            this.isMobileView = window.innerWidth < 768;
            // Set initial collapse state based on viewport
            if (this.isMobileView && !this.$data.hasOwnProperty('_collapseInitialized')) {
                this.collapseTranscription = true;
                this.$data._collapseInitialized = true;
            }
        },

        editChunkAtIndex(index) {
            this.chunkIndexBeingEdited = index;
            this.$nextTick(() => {
                document.getElementById('chunkEditingInput')?.focus();
            })
        },

        finishEditingChunkAtIndex(index) {
            this.chunkIndexBeingEdited = null;
            this.transcriptionChunks[index].text = this.transcriptionChunks[index].text.trim() + ' ';
            this.transcriptionChunks[index].pending_update = true;
        },

        saveJournalDraft() {
            this.$emit('saveJournalDraft');
        },

        async createTranscript() {
            if (this.transcript) {
                return;
            }

            if (! this.journalId) {
                this.saveJournalDraft();
                const time5SecondsLater = Date.now() + 5000;
                while (! this.journalId && (Date.now() < time5SecondsLater)) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
                if (! this.journalId) {
                    // Ideally, this should never happen, but just in case - let user know what to do.
                    alert('Could not save journal as draft, please reload the page and try again.');
                    return;
                }
            }

            try {
                await http.post('/app/ai/transcripts', {
                    transcriptable_type: 'journal',
                    transcriptable_id: this.journalId,
                }).then(({ data }) => {
                    this.transcript = data.data;
                }).catch((error) => {
                    console.error(error);
                    alert(error);
                });
            } catch (error) {
                console.error(error);
                this.persistingChunks = false;
            }
        },

        async persistTranscriptionChunks() {
            if (! this.hasTranscribedContent) {
                return;
            }

            this.persistingChunks = true;

            if (! this.transcript) {
                await this.createTranscript();
            }

            try {
                for (const chunkIndex in this.transcriptionChunks) {
                    const chunk = this.transcriptionChunks[chunkIndex];

                    if ((chunk.segment_id && ! chunk.pending_update) || ! chunk.text) {
                        continue;
                    }

                    if (chunk.segment_id && chunk.pending_update) {
                        await http.put(`/app/ai/transcripts/${this.transcript.uuid}/segments/${chunk.segment_id}`, {
                            content: chunk.text,
                        });

                        this.transcriptionChunks[chunkIndex].pending_update = false;
                    } else {
                        const { data } = await http.post(`/app/ai/transcripts/${this.transcript.uuid}/segments`, {
                            start_timestamp_ms: chunk.timestamp.getTime(),
                            content: chunk.text,
                        })

                        this.transcriptionChunks[chunkIndex].segment_id = data.data.segment_id;
                    }
                }
            } catch (error) {
                console.error(error);
                this.persistingChunks = false;
            }
        },

        async markTranscriptAsComplete() {
            if (! this.transcript) {
                return;
            }

            await http.post(`/app/ai/transcripts/${this.transcript.uuid}/complete`);
        },

        async markTranscriptAsInProgress() {
            if (! this.transcript) {
                return;
            }

            await http.post(`/app/ai/transcripts/${this.transcript.uuid}/in-progress`);
        },

        openSettings() {
            this.settingsOpened = ! this.settingsOpened;
        },

        addTranscriptionChunk(chunk) {
            this.transcriptionChunks.push({
                segment_id: null,
                text: chunk,
                timestamp: new Date(),
                pending_update: false,
            });
        },

        async generateSummary() {
            await this.persistTranscriptionChunks();

            let requestData = {
                transcript_uuid: this.transcript.uuid,
                output_language: this.selectedLanguage?.id || null,
                template: this.summaryTemplate?.key,
            };

            this.loadingSummary = true;

            return http.post('/app/ai/summary', requestData)
                .then(({ data }) => {
                    this.summary = data;
                    this.loadingSummary = false;
                    this.showAiSummaryDisclaimer = true;
                }).catch(({ error, errorBag }) => {
                    this.loadingSummary = false;
                    if (errorBag) {
                        this.$setErrorBag(errorBag);
                    }
                    console.error(error);
                });
        },

        // Handle transcription callbacks
        handleChunkReady(transcriptChunk, isNewChunk = true) {
            if (isNewChunk) {
                console.log('New chunk ready:', transcriptChunk);
                this.addTranscriptionChunk(transcriptChunk);
            } else {
                // Append to the last chunk
                const lastChunkIndex = this.transcriptionChunks.length - 1;
                if (lastChunkIndex >= 0) {
                    this.transcriptionChunks[lastChunkIndex].text += transcriptChunk;
                    this.transcriptionChunks[lastChunkIndex].pending_update = true;
                } else {
                    // If no chunks exist yet, create a new one
                    this.addTranscriptionChunk(transcriptChunk);
                }
            }
            this.scrollToBottom();
        },

        handleUnstableChunk(text, isNewChunk = false) {
            if (isNewChunk) {
                this.addTranscriptionChunk('');
            }

            this.scrollToBottom();
        },

        async startRecording() {
            if (this.transcriptionIsRecording) {
                return;
            }

            if (!this.transcript) {
                await this.createTranscript();
            }

            try {
                const success = await this.startTranscriptionRecording();
                if (success) {
                    this.markTranscriptAsInProgress();
                }
            } catch (error) {
                console.error(error);
            }
        },

        async stopRecording() {
            this.stopTranscriptionRecording();
            await this.markTranscriptAsComplete();
        },

        formatTime(date) {
            return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.transcriptionContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },

        transcriptionLog(type, data = {}) {
            http.post('/app/ai/transcription-log', { type, data, });
        },

        editTranscription() {
            this.editingTranscription = true;
        },

        fetchSummaryTemplates() {
            http.get('/app/ai/templates')
                .then(({ data }) => {
                    this.summaryTemplates = data;
                    const isPhysicaBrand = this.$store.state.brand === Brands.Physica;

                    // Update the selected template based on hasPreviousJournalEntries
                    if (this.hasPreviousJournalEntries) {
                        this.summaryTemplate = this.summaryTemplates.find(
                                template => isPhysicaBrand && template.key.startsWith('follow-up') && template.key.includes('physica')
                            ) || this.summaryTemplates.find(
                                template => template.key.startsWith('follow-up-'),
                            );
                    }

                    if (! this.summaryTemplate) {
                        this.summaryTemplate = this.summaryTemplates.find(
                                template => isPhysicaBrand && template.key.startsWith('first') && template.key.includes('physica')
                            ) || this.summaryTemplates.find(
                                template => template.key.startsWith('first-'),
                            ) || this.summaryTemplates[0];
                    }
                })
                .catch((error) => {
                    console.error('Failed to fetch summary templates:', error);
                });
        },
    },

    computed: {
        canUseTranscription() {
            return this.$store.state.transcriptions?.enabled;
        },

        hasTranscribedContent() {
            return this.transcriptionChunks.length > 0 || this.transcriptionUnstableChunk.length > 0;
        },

        lastTranscriptionWords() {
            if (! this.collapseTranscription) {
                return '';
            }

            const numberOfWords = Math.max(0, 12 - this.transcriptionUnstableChunk.split(' ').length);

            if (numberOfWords <= 0) {
                return '';
            }

            return this.fullTranscription.split(' ').slice(-numberOfWords).join(' ');
        },

        fullTranscription() {
            return this.transcriptionChunks.map((chunk) => chunk.text).join('');
        },

        fullTranscriptionForEditing() {
            return this.transcriptionChunks.map((chunk) => chunk.text).join('');
        },

        elevatorNoteOptions() {
            switch (this.summaryTemplate.type) {
                case SummaryPromptType.FirstVisit:
                    return Object.values(trans('modules.Ai.elevator_notes.first_visit'));

                case SummaryPromptType.FollowUpVisit:
                    return Object.values(trans('modules.Ai.elevator_notes.follow_up'));

                default:
                    return [];
            }
        },

        elevatorNoteSpeed() {
            switch (this.summaryTemplate.type) {
                case SummaryPromptType.FirstVisit:
                    return 4500;

                case SummaryPromptType.FollowUpVisit:
                    return 4500;

                default:
                    return 3000;
            }
        },
    },

    watch: {
        summary() {
            this.$emit('summary', this.summary);
        },
    },
};
</script>

<style>
.transcription-container {
    border: 2px solid transparent;
    border-radius: 10px;
    -webkit-background-image: linear-gradient(#FFFFFFF2, #FFFFFFF2), linear-gradient(to bottom right, #800b7295, var(--primary-color));
    background-image: linear-gradient(#FFFFFFF2, #FFFFFFF2), linear-gradient(to bottom right, #800b7295, var(--primary-color));
    background-origin: border-box;
    background-clip: padding-box, border-box;
}

.transcription-settings-menu {
    z-index: 1001;
    position: absolute;
    right: 0;
    top: 37px;
    width: 300px;
    background: white;
}

@media (max-width: 768px) {
    .transcription-container {
        padding: 1rem !important;
    }

    .transcription-settings-menu {
        right: -50px;
        width: 280px;
        max-width: calc(100vw - 40px);
    }
}
</style>
